// Alpine component for a wrapper that contains one or more .scroll-image__img children.
// Drives their object-position from body scroll when scroll-linked animations aren't supported.

function scrollImageFallback({ minWidth = 768, headerOffset = 0, smooth = true } = {}) {
  return {
    minWidth, // desktop-only breakpoint
    headerOffset, // sticky header height if any
    smooth, // use rAF throttle

    _rafId: null,
    _needsFrame: false,
    _start: 0,
    _end: 1,
    _images: [],

    init() {
      // Bail if scroll-linked animations are supported
      if (CSS.supports("animation-timeline: scroll()")) return;

      // ** RF14 A/B test  **
      // The stacked gallery A/B variant (lg+) sizes each image to its own cell, so there is nothing to pan - treat it the same as "not desktop" and stay torn down.
      const isStackedGallery = () =>
        document.documentElement.classList.contains("gallery-stacked") &&
        window.matchMedia("(min-width: 1024px)").matches;

      const isDesktop = () => window.matchMedia(`(min-width: ${this.minWidth}px)`).matches && !isStackedGallery();
      if (!isDesktop()) return;

      // Gather child images inside this wrapper
      this._images = Array.from(this.$el.querySelectorAll(".scroll-image__img"));
      if (!this._images.length) return;

      // Baseline: match @supports not(...) CSS
      this._images.forEach((img) => {
        img.style.objectPosition = "50% 0%";
      });

      const computeBounds = () => {
        // Use wrapper’s position as the scroll reference
        const rect = this.$el.getBoundingClientRect();
        const docTop = rect.top + window.scrollY;
        const height = this.$el.offsetHeight;

        // Start when wrapper top reaches viewport top (minus header offset)
        this._start = docTop - this.headerOffset;

        // End when wrapper bottom reaches viewport top
        this._end = docTop + height - this.headerOffset;

        if (this._end <= this._start) this._end = this._start + 1;
      };

      const tick = () => {
        const y = window.scrollY;
        const raw = (y - this._start) / (this._end - this._start);
        const clamped = Math.max(0, Math.min(1, raw));

        this._images.forEach((img) => {
          img.style.objectPosition = `50% ${clamped * 100}%`;
        });
      };

      const measure = () => {
        computeBounds();
        tick();
      };

      const schedule = () => {
        if (!this.smooth) {
          tick();
          return;
        }
        if (this._needsFrame) return;
        this._needsFrame = true;
        this._rafId = requestAnimationFrame(() => {
          this._needsFrame = false;
          tick();
        });
      };

      const onScroll = () => schedule();
      const onResize = () => {
        if (!isDesktop()) {
          cleanup();
          return;
        }
        measure();
      };

      const cleanup = () => {
        if (this._rafId) cancelAnimationFrame(this._rafId);
        window.removeEventListener("scroll", onScroll);
        window.removeEventListener("resize", onResize);
      };

      window.addEventListener("scroll", onScroll, { passive: true });
      window.addEventListener("resize", onResize, { passive: true });

      measure(); // initial call

      // Alpine teardown
      this.$el._scrollImageCleanup = cleanup;
    },

    destroy() {
      if (this.$el._scrollImageCleanup) this.$el._scrollImageCleanup();
    }
  };
}

export default scrollImageFallback;
